// The tenant's identity: its name, its logo, and the colours the whole
// interface is painted in.
//
// Colours arrive as platform settings and are published as CSS custom
// properties, so components style themselves from tokens and never read this
// context to pick a colour. A tenant that has set nothing is shown as MyMentor,
// in MyMentor's green.

import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { get } from './api'
import type { PublicPlatform } from './api'

const PlatformContext = createContext<PublicPlatform | null>(null)

/** The name shown when a tenant has none of its own. */
export const PRODUCT_NAME = 'MyMentor'

const FALLBACK: PublicPlatform = {
  name: PRODUCT_NAME,
  locale: 'uz',
  colors: {},
}

export function PlatformProvider({ children }: { children: ReactNode }) {
  const [platform, setPlatform] = useState<PublicPlatform | null>(null)

  useEffect(() => {
    const ac = new AbortController()
    get<PublicPlatform>('/platform/public', ac.signal)
      .then((p) => setPlatform({ ...p, name: p.name?.trim() || PRODUCT_NAME, colors: p.colors ?? {} }))
      // A tenant that cannot be read still gets a usable, unbranded interface
      // rather than a blank page.
      .catch(() => setPlatform(FALLBACK))
    return () => ac.abort()
  }, [])

  useEffect(() => {
    if (!platform) return
    applyTheme(platform)
    document.title = platform.name
    if (platform.favicon_path) setFavicon(platform.favicon_path)
  }, [platform])

  const value = useMemo(() => platform, [platform])
  return <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>
}

export function usePlatform(): PublicPlatform {
  return useContext(PlatformContext) ?? FALLBACK
}

/** True until the tenant's branding has arrived; used to hold back the first paint. */
export function usePlatformReady(): boolean {
  return useContext(PlatformContext) !== null
}

function applyTheme(p: PublicPlatform) {
  const root = document.documentElement
  const set = (token: string, value?: string) => {
    if (value && isColor(value)) root.style.setProperty(token, value)
  }

  set('--ok', p.colors.success)
  set('--warn', p.colors.warning)
  set('--crit', p.colors.error)

  // Light and dark are published under separate prefixes; the stylesheet picks
  // between them, so both are set regardless of what the viewer is currently in.
  set('--light-ground', p.colors.light?.background)
  set('--light-ink', p.colors.light?.text)
  set('--light-footer', p.colors.light?.footer)
  set('--dark-ground', p.colors.dark?.background)
  set('--dark-ink', p.colors.dark?.text)
  set('--dark-footer', p.colors.dark?.footer)

  publishAccent(p)
}

/**
 * The primary is the one token with a value per theme: a deep green that reads
 * well on white disappears on near-black. An inline property would win over
 * both themes at once, so the accent is published as a small stylesheet of its
 * own instead — one rule per theme, added after ours so it wins in each, and
 * each with the text colour that stays legible on it.
 *
 * Without a dark primary of the tenant's own, the dark theme gets a lighter
 * shade of the light one; without any primary the stylesheet's MyMentor green
 * stands.
 */
function publishAccent(p: PublicPlatform) {
  const light = validColor(p.colors.light?.primary) ?? validColor(p.colors.primary)
  const dark = validColor(p.colors.dark?.primary) ?? (light ? lightenForDark(light) : null)

  const id = 'tenant-accent'
  let sheet = document.getElementById(id) as HTMLStyleElement | null
  if (!light && !dark) {
    sheet?.remove()
    return
  }
  if (!sheet) {
    sheet = document.createElement('style')
    sheet.id = id
    document.head.appendChild(sheet)
  }
  const rules: string[] = []
  if (light) rules.push(`:root { --accent: ${light}; --on-accent: ${textOn(light)}; }`)
  if (dark) {
    const d = `--accent: ${dark}; --on-accent: ${textOn(dark)};`
    rules.push(`@media (prefers-color-scheme: dark) { :root:not([data-theme='light']) { ${d} } }`)
    rules.push(`:root[data-theme='dark'] { ${d} }`)
  }
  sheet.textContent = rules.join('\n')
}

/** Guards against a stored value being pushed into the stylesheet unchecked. */
function isColor(v: string): boolean {
  return /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(v.trim())
}

function validColor(v: string | undefined): string | null {
  return v && isColor(v) ? expand(v.trim()) : null
}

/** #f60 → #ff6600, so the arithmetic below reads one shape. */
function expand(hex: string): string {
  return hex.length === 4 ? `#${[...hex.slice(1)].map((c) => c + c).join('')}` : hex
}

function rgbOf(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1), 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

/** Relative luminance, as WCAG defines it. */
function luminance(hex: string): number {
  const [r, g, b] = rgbOf(hex).map((v) => {
    const c = v / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }) as [number, number, number]
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/**
 * White on a dark primary, near-black on a light one. White keeps a narrow
 * tie: on a mid-tone blue or green it is what every button has always had,
 * and it only gives way when dark text reads clearly better.
 */
function textOn(hex: string): string {
  const l = luminance(hex)
  const onWhite = 1.05 / (l + 0.05)
  const onDark = (l + 0.05) / 0.0625
  return onDark > onWhite * 1.15 ? '#062019' : '#ffffff'
}

/**
 * The same hue, light enough to read on a dark page: lightness raised to about
 * 60% and saturation calmed so a vivid colour does not glare.
 */
function lightenForDark(hex: string): string {
  const [r, g, b] = rgbOf(hex).map((v) => v / 255) as [number, number, number]
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l0 = (max + min) / 2
  if (max !== min) {
    const d = max - min
    s = l0 > 0.5 ? d / (2 - max - min) : d / (max + min)
    h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4
    h /= 6
  }
  const l = Math.max(l0, 0.6)
  const sat = Math.min(s, 0.55)
  const q = l < 0.5 ? l * (1 + sat) : l + sat - l * sat
  const p = 2 * l - q
  const channel = (t: number) => {
    const x = t < 0 ? t + 1 : t > 1 ? t - 1 : t
    if (x < 1 / 6) return p + (q - p) * 6 * x
    if (x < 1 / 2) return q
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6
    return p
  }
  const out = [channel(h + 1 / 3), channel(h), channel(h - 1 / 3)]
  return `#${out.map((v) => Math.round(v * 255).toString(16).padStart(2, '0')).join('')}`
}

function setFavicon(href: string) {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  if (!link) {
    link = document.createElement('link')
    link.rel = 'icon'
    document.head.appendChild(link)
  }
  link.removeAttribute('type')
  link.href = href
}
